import { Link } from "react-router-dom";
import { useData } from "@/context/DataContext";
import { useAuth } from "@/context/AuthContext";
import { ProgressRing } from "@/components/ProgressRing";
import { MILESTONES, isDone, milestoneProgress, nextMove, overallPercent } from "@/constants/milestones";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TrainingTab } from "@/components/academy/TrainingTab";
import { RecipesTab } from "@/components/academy/RecipesTab";
import { EventPrepTab } from "@/components/academy/EventPrepTab";
import { HelpTab, ResourcesTab } from "@/components/academy/InfoTabs";
import { ArrowRight, Check, Lock, LogOut } from "lucide-react";
import { BRAND, BrandMarquee } from "@/constants/brand";
import { FREE_MILESTONES, useAccess } from "@/lib/access";
import { FreePreviewBanner, JourneyStartedPanel, LockedPanel } from "@/components/Locked";

const TABS = [
  ["training", "Training", false],
  ["recipes", "Recipes", true],
  ["prep", "Event Prep", true],
  ["resources", "Resources", true],
  ["help", "Get Help", false],
];

export default function Academy() {
  const { content, state } = useData();
  const { user, logout } = useAuth();
  const { paidAcademy } = useAccess();

  if (!content || !state)
    return <div className="p-10 mono text-sm text-muted-foreground">Loading the academy…</div>;

  const percent = overallPercent(content, state);
  const next = nextMove(content, state);

  return (
    <div data-testid="academy-page">
      <div className="relative h-56 overflow-hidden border-b border-border md:h-72">
        <div
          className="absolute inset-0 photo-band"
          style={{ backgroundImage: `url(${BRAND.photos.lemonSplash})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/80 to-[#0A0A0A]/45" />
        <div className="relative mx-auto flex h-full max-w-7xl items-end justify-between gap-6 px-5 pb-6 md:px-10">
          <div>
            <div className="mono text-[11px] tracking-[0.35em] text-primary">
              SMASH LEMONADE · TRAINING MATERIAL
            </div>
            <h1 className="neon mt-1 text-5xl leading-none md:text-6xl">Operator Academy</h1>
            <p className="brand-script text-2xl text-primary md:text-3xl">{BRAND.mantra}</p>
          </div>
          <img
            src={BRAND.logo}
            alt="Smash Lemonade"
            className="hidden w-40 self-center drop-shadow-[0_6px_24px_rgba(0,0,0,0.9)] md:block"
          />
        </div>
      </div>
      <BrandMarquee />

      <div className="mx-auto max-w-7xl px-5 py-8 md:px-10 md:py-12">
      <header className="flex flex-wrap items-start justify-between gap-6">
        <div>
          <div className="mono text-[11px] tracking-[0.35em] text-primary">
            SIGNED IN AS {(user.name || user.email).toUpperCase()}
          </div>
          <h2 className="mt-1 text-4xl uppercase leading-none">Your progress</h2>
          <p className="mt-2 max-w-xl text-sm text-muted-foreground">{BRAND.quote}</p>
        </div>
        <div className="flex items-center gap-6">
          <ProgressRing percent={percent} />
          <div className="flex flex-col items-end gap-3">
            <Link
              to="/our-story"
              data-testid="founders-link"
              className="mono text-[11px] tracking-widest text-primary hover:underline"
            >
              FOUNDERS
            </Link>
            <Link
              to="/academy"
              data-testid="pricing-link"
              className="mono text-[11px] tracking-widest text-muted-foreground hover:text-primary transition-colors"
            >
              PRICING
            </Link>
            <Link
              to="/hq"
              data-testid="hq-link"
              className="mono text-[11px] tracking-widest text-muted-foreground hover:text-primary transition-colors"
            >
              SMASH HQ →
            </Link>
            <button
              data-testid="logout-btn"
              onClick={logout}
              className="mono flex items-center gap-1 text-[11px] tracking-widest text-muted-foreground hover:text-destructive transition-colors"
            >
              <LogOut size={12} /> LOG OUT
            </button>
          </div>
        </div>
      </header>

      {!paidAcademy &&
        (FREE_MILESTONES.every((n) => isDone(MILESTONES[n - 1], content, state)) ? (
          <JourneyStartedPanel />
        ) : (
          <FreePreviewBanner />
        ))}

      <section
        className="relative mt-10 overflow-hidden border border-primary/50 rounded-sm p-6 md:p-8 fade-up"
        data-testid="next-move-card"
      >
        <div
          className="absolute inset-0 photo-band opacity-30"
          style={{ backgroundImage: `url(${BRAND.photos.graffitiTags})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#191603] via-[#191603]/95 to-[#191603]/55" />
        <div className="relative">
          <div className="mono text-[11px] tracking-[0.3em] text-primary">YOUR NEXT MOVE</div>
          <h2 className="display neon mt-2 text-3xl leading-tight md:text-5xl">{next.label}</h2>
          {next.milestone && (
            <div className="mono mt-3 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
              <span>MILESTONE {next.milestone.n}</span>
              <ArrowRight size={12} />
              <span>{next.milestone.title.toUpperCase()}</span>
              {next.sub && <span className="text-primary">· {next.sub}</span>}
            </div>
          )}
        </div>
      </section>

      <section className="mt-12">
        <h2 className="display text-3xl uppercase">Milestone roadmap</h2>
        <div className="mt-4 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {MILESTONES.map((m) => {
            const { done, total } = milestoneProgress(m, content, state);
            const complete = isDone(m, content, state);
            const locked = !paidAcademy && !FREE_MILESTONES.includes(m.n);
            if (locked)
              return (
                <div
                  key={m.n}
                  data-testid={`milestone-locked-${m.n}`}
                  className="relative rounded-sm border border-border bg-[#101010] p-5"
                >
                  <div className="flex items-start justify-between">
                    <span className="display text-5xl leading-none text-muted-foreground/40">
                      {String(m.n).padStart(2, "0")}
                    </span>
                    <Lock size={14} className="mt-2 text-primary" />
                  </div>
                  <div className="display mt-3 text-2xl uppercase text-muted-foreground">
                    {m.title}
                  </div>
                  <p className="text-sm text-muted-foreground/70">{m.blurb}</p>
                  <Link
                    to="/buy/popup_profits"
                    data-testid={`milestone-unlock-${m.n}`}
                    className="mono mt-5 inline-block text-[10px] tracking-widest text-primary hover:underline"
                  >
                    UNLOCK SMASH ACADEMY →
                  </Link>
                </div>
              );
            return (
              <div
                key={m.n}
                data-testid={`milestone-${m.n}`}
                className={`rounded-sm border p-5 transition-all hover:-translate-y-1 ${
                  complete ? "border-[#34C759]/60 bg-[#0f1710]" : "border-border bg-[#141414] hover:border-primary"
                }`}
              >
                <div className="flex items-start justify-between">
                  <span className="display text-5xl leading-none text-primary/70">
                    {String(m.n).padStart(2, "0")}
                  </span>
                  {complete ? (
                    <span
                      className="mono flex items-center gap-1 text-[10px] tracking-widest text-[#34C759]"
                      data-testid={`milestone-done-${m.n}`}
                    >
                      <Check size={12} /> DONE
                    </span>
                  ) : m.type === "checkoff" ? (
                    <span className="mono border border-primary/60 px-2 py-0.5 text-[9px] tracking-widest text-primary">
                      CHECKOFF REQUIRED
                    </span>
                  ) : null}
                </div>
                <div className="display mt-3 text-2xl uppercase">{m.title}</div>
                <p className="text-sm text-muted-foreground">{m.blurb}</p>
                <div className="mt-4 h-1 w-full bg-[#262626]">
                  <div
                    className="h-1 bg-primary transition-all"
                    style={{ width: `${Math.round((done / total) * 100)}%` }}
                  />
                </div>
                <div className="mono mt-2 text-[10px] text-muted-foreground">
                  {done}/{total} COMPLETE
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="mt-14">
        <Tabs defaultValue="training">
          <TabsList className="flex h-auto w-full flex-wrap justify-start gap-2 bg-transparent p-0">
            {TABS.map(([v, label, gated]) => (
              <TabsTrigger
                key={v}
                value={v}
                data-testid={`tab-${v}`}
                className="display flex items-center gap-2 rounded-sm border border-border bg-[#141414] px-5 py-2 text-xl uppercase text-muted-foreground data-[state=active]:border-primary data-[state=active]:bg-primary data-[state=active]:text-primary-foreground transition-colors"
              >
                {label}
                {gated && !paidAcademy ? <Lock size={13} /> : null}
              </TabsTrigger>
            ))}
          </TabsList>
          <div className="mt-6">
            <TabsContent value="training"><TrainingTab /></TabsContent>
            <TabsContent value="recipes">
              {paidAcademy ? <RecipesTab /> : <LockedPanel testId="locked-recipes" title="Recipe Vault is locked" />}
            </TabsContent>
            <TabsContent value="prep">
              {paidAcademy ? <EventPrepTab /> : <LockedPanel testId="locked-prep" title="Event Prep is locked" />}
            </TabsContent>
            <TabsContent value="resources">
              {paidAcademy ? <ResourcesTab /> : <LockedPanel testId="locked-resources" title="Resource Vault is locked" />}
            </TabsContent>
            <TabsContent value="help"><HelpTab /></TabsContent>
          </div>
        </Tabs>
      </section>
      </div>
    </div>
  );
}
